import React, { useState } from 'react';
import AvatarChip from './avatar-chip';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';

interface FieldValueDisplayProps {
  field: string;
  value: React.ReactNode;
}

function FieldValueDisplay({ field, value }: FieldValueDisplayProps) {
  return (
    <Grid container spacing={1} sx={{ mb: 1 }}>
      <Grid size={{ xs: 3 }}>
        <Typography
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            fontWeight: 'light',
          }}
        >
          {field}
        </Typography>
      </Grid>
      <Grid size={{ xs: 9 }}>{value}</Grid>
    </Grid>
  );
}

function ArrayValueDisplay({ items }: { items: React.ReactNode[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length <= 5) {
    return (
      <Stack spacing={0.5}>
        {items.map((item, index) => (
          <div key={index}>{item}</div>
        ))}
      </Stack>
    );
  }

  if (isExpanded) {
    return (
      <Stack spacing={0.5}>
        {items.map((item, index) => {
          if (index === items.length - 1) {
            return (
              <Stack
                direction="row"
                key={index}
                alignItems="center"
                spacing={1}
              >
                <div>{item}</div>
                <Chip
                  label=""
                  icon={<ExpandLess />}
                  onClick={() => setIsExpanded(false)}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    height: '25px',
                    '& .MuiChip-label': {
                      paddingRight: 0,
                    },
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </Stack>
            );
          }
          return <div key={index}>{item}</div>;
        })}
      </Stack>
    );
  }

  const remainingCount = items.length - 5;
  const collapsedItems = items.slice(0, 5);
  return (
    <Stack spacing={0.5}>
      {collapsedItems.map((item, index) => {
        if (index === collapsedItems.length - 1) {
          return (
            <Stack direction="row" key={index} alignItems="center" spacing={1}>
              <div>{item}</div>
              <Chip
                label={`+${remainingCount}`}
                onClick={() => setIsExpanded(true)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: 'action.hover' },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Stack>
          );
        }
        return <div key={index}>{item}</div>;
      })}
    </Stack>
  );
}

interface ModelDetailsDisplayProps {
  title: string;
  data: Record<string, React.ReactNode>;
}

export default function ModelDetailsDisplay({
  title,
  data,
}: ModelDetailsDisplayProps) {
  const theme = useTheme();
  const formatFieldName = (fieldName: string) => {
    if (fieldName.includes(' ')) {
      return fieldName;
    }
    const result = fieldName.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  return (
    <>
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', mb: 1, mt: 2, fontSize: 14 }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'transparent' : 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {Object.entries(data).map(([key, value]) => {
          let displayValue = value;
          if (key === 'creator' && typeof value === 'string' && value) {
            displayValue = (
              <AvatarChip key={value} email={value} label={value} type="User" />
            );
          } else if (key === 'createdDate' && typeof value === 'number') {
            displayValue = new Date(value).toLocaleString();
          } else if (Array.isArray(value) && value.length > 0) {
            displayValue = <ArrayValueDisplay items={value} />;
          }

          if (value === null || value === undefined || value === '') {
            displayValue = 'N/A';
          }

          return (
            <FieldValueDisplay
              key={key}
              field={formatFieldName(key)}
              value={displayValue}
            />
          );
        })}
      </Box>
    </>
  );
}
